import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useHistory } from 'react-router-dom';
import { format, differenceInDays } from 'date-fns';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import { dateFormat } from 'constants/formats';

import Button from 'modules/_shared/components/Buttons';

import { IncentiveOfferType } from 'modules/incentivesMarket/types';
import { isPartialMinSharesValid } from 'modules/incentivesMarket/utils/getPurchaseQuantityBounds';
import { getSellerLimitsModeLabelKey } from 'modules/incentivesMarket/utils/getSellerLimitsSummary';
import { useAddIncentiveOfferPage } from '../context/AddIncentiveOfferPageContext';

const AddIncentiveOfferResumeSection: FC = () => {
  const { t } = useTranslate();
  const history = useHistory();
  const { data, actions } = useAddIncentiveOfferPage();

  const isSellOffer = data.incentiveOfferType === IncentiveOfferType.SELL;

  const isValidateDates =
    data.startDate &&
    data.endDate &&
    new Date(data.startDate) < new Date(data.endDate);

  const isPartialMinValid = isPartialMinSharesValid(
    data.isPartialAmountEnabled,
    data.minUnitsPerAcceptance,
    data.sharesToTransfer
  );

  const isReviewDisabled =
    !data.sharesToTransfer ||
    !data.pricePerShare ||
    !isValidateDates ||
    !isPartialMinValid;

  const offerDuration = useMemo(() => {
    if (!data.startDate || !data.endDate) return '-';
    const days = differenceInDays(
      new Date(data.endDate),
      new Date(data.startDate)
    );
    return t('NDays', { count: days });
  }, [data.startDate, data.endDate, t]);

  const summaryTitle = isSellOffer
    ? t('YourSellOfferSummary')
    : t('YourBuyOfferSummary');

  const typeBadgeClass = isSellOffer
    ? 'badge badge-dim badge-info'
    : 'badge badge-dim badge-primary';

  const typeLabel = isSellOffer ? t('SellOffer') : t('BuyOffer');

  const unitsAfterSale = isSellOffer
    ? Math.max(0, data.maxSharesToTransfer - data.sharesToTransfer)
    : 0;

  return (
    <div className="col-lg-5">
      <div
        className="card card-bordered sticky-top summary-sticky"
        style={{ top: '80px' }}
      >
        <div className="card-inner">
          <div className="text-center mb-3">
            <em className="icon ni ni-offer fs-24px text-info" />
            <h5 className="mt-2 mb-1">{t(summaryTitle)}</h5>
            <p className="text-soft fs-13px mb-0">
              {t('ReviewDetailsBeforePublishing')}
            </p>
          </div>

          <hr />

          <div className="summary-line">
            <span className="text-soft">{t('Type')}</span>
            <strong>
              <span className={typeBadgeClass}>{typeLabel}</span>
            </strong>
          </div>

          {data.beneficiaryCif && data.plansBreakdown.length > 1 && (
            <div className="summary-line">
              <span className="text-soft">{t('IncentivesMarketCifGroup')}</span>
              <strong>{data.beneficiaryCif}</strong>
            </div>
          )}

          <div className="summary-line">
            <span className="text-soft">{t('IncentivesMarketUnitsNumber')}</span>
            <strong>{formatNumber(data.sharesToTransfer)}</strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('IncentivesMarketPricePerUnit')}</span>
            <strong>{formatCurrencyDecimals(data.pricePerShare)}</strong>
          </div>

          <div className="summary-line border-top pt-2">
            <span className="text-soft">{t('TotalPrice')}</span>
            <strong className="text-primary fs-18px">
              {formatCurrencyDecimals(data.totalOfferPrice)}
            </strong>
          </div>

          <hr />

          <div className="summary-line">
            <span className="text-soft">{t('StartDate')}</span>
            <strong>
              {data.startDate
                ? format(new Date(data.startDate), dateFormat)
                : '-'}
            </strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('EndDate')}</span>
            <strong>
              {data.endDate ? format(new Date(data.endDate), dateFormat) : '-'}
            </strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('Duration')}</span>
            <strong>{offerDuration}</strong>
          </div>

          <div className="summary-line">
            <span className="text-soft">{t('PartialAcceptance')}</span>
            <strong>
              {data.isPartialAmountEnabled ? t('Yes') : t('Not')}
              {data.isPartialAmountEnabled &&
                data.minUnitsPerAcceptance > 0 && (
                  <span className="text-soft fs-13px ml-1">
                    (mín. {data.minUnitsPerAcceptance})
                  </span>
                )}
            </strong>
          </div>

          {!isSellOffer && (
            <div className="summary-line">
              <span className="text-soft">
                {t('IncentivesMarketSellerLimitsTitle')}
              </span>
              <strong>{t(getSellerLimitsModeLabelKey(data.sellerParticipationLimits))}</strong>
            </div>
          )}

          <hr />

          {isSellOffer && data.sharesToTransfer > 0 && (
            <div className="alert alert-light mb-3">
              <h6 className="alert-heading fs-13px">
                {t('AfterPublishingOffer')}:
              </h6>
              <ul className="list fs-13px mb-0">
                <li className="list-item pb-0">
                  {t('IncentivesMarketUnitsWillBeReserved', {
                    count: formatNumber(data.sharesToTransfer),
                  })}
                </li>
                <li className="list-item pb-0">
                  {t('IncentivesMarketUnitsRemainingAfterOffer', {
                    count: formatNumber(unitsAfterSale),
                  })}
                </li>
                <li className="list-item pb-0">
                  {t('YouCanCancelOfferAnytime')}
                </li>
              </ul>
            </div>
          )}

          <Button
            className="btn-block"
            onClick={actions.handleReviewIncentiveOffer}
            disabled={isReviewDisabled}
            lgSize
          >
            <em className="icon ni ni-send mr-2" />
            {t('ReviewOffer')}
          </Button>

          <div className="text-center mt-2">
            <button
              type="button"
              className="btn btn-link text-soft fs-13px"
              onClick={() =>
                history.push(`/mercado-de-incentivos/${data.societyId}`)
              }
            >
              <em className="icon ni ni-arrow-left mr-1" />
              {t('Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddIncentiveOfferResumeSection;
